import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { fetchQuery } from "../../../../utils/commonComponent/fetched";
import { generateOfferJoiningPDF } from "./offerJoiningPDF";
import DownloadPdfLoaderModal from "../../../../utils/commonComponent/downloadPdfLoader";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { formateDate } from "../../../../utils/commonComponent/commonConstant";

import ContractOfferPDF from "./components/contractOfferPDF";
import FulltimeOfferPDF from "./components/fulltimeOfferPDF";
import FulltimeApptPDF from "./components/fulltimeApptPDF";
import ContractJoiningPDF from "./components/contractJoiningPDF";
import FulltimeJoiningPDF from "./components/fulltimeJoiningPDF";
import { calculateContractSalary, calculateFulltimeSalary } from "./offerJoiningUtils";

export const config = {
  refPrefix: "DYC/HR-OFFER-PR/",
  contractRefPrefix: "DYC/HR-OFFER-FTC/",
  apptRefPrefix: "DYC/HR-APPT/",
  jkRefPrefix: "DYC/HR-JK/",
};

const ViewOfferJoining = (props) => {
  const params = useParams();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const rawDocParam = queryParams.get("doc");
  const activeTab = rawDocParam === "joining" ? "joining" : rawDocParam === "appointment" ? "appointment" : "offer";

  const [inputStates, setInputStates] = useState(null);
  const [pipelineInfo, setPipelineInfo] = useState({});
  const [downloadPdfloading, setDownloadPdfloading] = useState(false);

  const fetchDetails = async () => {
    try {
      const [record] = await fetchQuery(
        `SELECT * FROM talentAcqOffer WHERE id = ${params.id}`
      );
      if (record) {
        setInputStates(record);
        if (record.pipelineId) {
          const [pipe] = await fetchQuery(
            `SELECT p.id, p.pipelineId as plCode, c.*, m.Value as empTypeName, r.requestedId as reqCode, p.position, p.client 
             FROM talentAcqPipeline p 
             LEFT JOIN talentAcqCandidate c ON p.candId = c.id 
             LEFT JOIN talentAcquisitionMaster m ON c.employmentType = m.id
             LEFT JOIN talentAcqRequirement r ON p.requirementId = r.id
             WHERE p.id = ${record.pipelineId}`
          );
          if (pipe) setPipelineInfo(pipe);
        }
      }
    } catch (error) {
      console.error("Error fetching offer joining details:", error);
    }
  };

  useEffect(() => {
    if (params.id) fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  if (!inputStates) {
    return <div className="p-6 text-center text-gray-500">Loading Details...</div>;
  }

  const cCalc = calculateContractSalary(inputStates);
  const fCalc = calculateFulltimeSalary(inputStates);

  const isContract =
    (pipelineInfo.empTypeName && pipelineInfo.empTypeName.toLowerCase().includes("contract")) ||
    cCalc.cFeesM > 0 ||
    cCalc.cOtherM > 0;

  const candidateName = inputStates.candidateName || pipelineInfo.candidateName || "Candidate Name";
  const candidateAddress = inputStates.address || pipelineInfo.currentLocation || pipelineInfo.address || "";
  const candidateMobile = inputStates.mobile || pipelineInfo.mobileNo || pipelineInfo.mobileNumber || "";
  const candidateEmail = inputStates.email || pipelineInfo.emaiId || pipelineInfo.emailId || "";
  const candidateLocation = pipelineInfo.client || pipelineInfo.currentLocation || pipelineInfo.location || "Dahej, Gujarat";
  const position = inputStates.position || pipelineInfo.position || "Sr. Inspection Engineer";

  const offerRefCode = isContract
    ? `${config.contractRefPrefix}${String(inputStates.id).padStart(3, "0")}`
    : `${config.refPrefix}${String(inputStates.id).padStart(3, "0")}`;

  const apptRefCode = `${config.apptRefPrefix}${String(inputStates.id).padStart(3, "0")}`;
  const jkRefCode = `${config.jkRefPrefix}DYC-${String(inputStates.id).padStart(4, "0")}`;

  const offerDateFormatted = formateDate(inputStates.offerDate) || "24/03/2026";
  const joiningDateFormatted = formateDate(inputStates.confirmedJoiningDate) || formateDate(inputStates.actualJoiningDate) || "06/04/2026";

  const getDocumentTitle = () => {
    if (activeTab === "appointment") {
      return "Appointment Letter";
    }
    if (activeTab === "joining") {
      return "Joining Letter";
    }
    return isContract ? "Contract Letter" : "Offer Letter";
  };

  const downloadButtonLabel = `Download ${getDocumentTitle()}`;

  const fullDataPayload = {
    ...inputStates,
    candidateName,
    candidateAddress,
    candidateMobile,
    candidateEmail,
    candidateLocation,
    position,
    offerRefCode,
    apptRefCode,
    jkRefCode,
    offerDateFormatted,
    joiningDateFormatted,
    isContract,
    docType: activeTab,
    cCalc,
    fCalc,
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Top Action Bar */}
      <div className="flex justify-end items-center mb-4">
        <button
          onClick={() =>
            generateOfferJoiningPDF(fullDataPayload, setDownloadPdfloading)
          }
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1.5 font-medium shadow-sm"
        >
          <PictureAsPdfIcon fontSize="small" />
          {downloadButtonLabel}
        </button>
      </div>

      {/* Main Document Preview Card */}
      <div className="bg-white p-6 rounded-md border border-gray-300 shadow-sm flex flex-col gap-6">
        {activeTab === "joining" ? (
          isContract ? (
            <ContractJoiningPDF
              candidateName={candidateName}
              candidateAddress={candidateAddress}
              candidateMobile={candidateMobile}
              candidateEmail={candidateEmail}
              jkRefCode={jkRefCode}
              offerDateFormatted={offerDateFormatted}
            />
          ) : (
            <FulltimeJoiningPDF
              candidateName={candidateName}
              candidateAddress={candidateAddress}
              candidateMobile={candidateMobile}
              candidateEmail={candidateEmail}
              jkRefCode={jkRefCode}
              offerDateFormatted={offerDateFormatted}
            />
          )
        ) : activeTab === "appointment" && !isContract ? (
          <FulltimeApptPDF
            candidateName={candidateName}
            candidateAddress={candidateAddress}
            candidateMobile={candidateMobile}
            candidateEmail={candidateEmail}
            candidateLocation={candidateLocation}
            position={position}
            offerRefCode={offerRefCode}
            apptRefCode={apptRefCode}
            offerDateFormatted={offerDateFormatted}
            joiningDateFormatted={joiningDateFormatted}
            {...inputStates}
            {...fCalc}
          />
        ) : isContract ? (
          <ContractOfferPDF
            candidateName={candidateName}
            candidateAddress={candidateAddress}
            candidateMobile={candidateMobile}
            candidateEmail={candidateEmail}
            candidateLocation={candidateLocation}
            position={position}
            offerRefCode={offerRefCode}
            offerDateFormatted={offerDateFormatted}
            joiningDateFormatted={joiningDateFormatted}
            {...cCalc}
          />
        ) : (
          <FulltimeOfferPDF
            candidateName={candidateName}
            candidateAddress={candidateAddress}
            candidateMobile={candidateMobile}
            candidateEmail={candidateEmail}
            candidateLocation={candidateLocation}
            position={position}
            offerRefCode={offerRefCode}
            offerDateFormatted={offerDateFormatted}
            joiningDateFormatted={joiningDateFormatted}
            note16={inputStates?.note16 || inputStates?.notes16}
            note17={inputStates?.note17 || inputStates?.notes17}
            {...fCalc}
          />
        )}
      </div>

      {downloadPdfloading && (
        <DownloadPdfLoaderModal loading={downloadPdfloading} name={getDocumentTitle()} />
      )}
    </div>
  );
};

export default ViewOfferJoining;
